import { useState } from 'react';
import { Link } from 'react-router';
import { api, errorMessage } from '../../shared/api/client';
import type { Visitor, VisitorStage } from '../../shared/api/types';
import { Alert, Button, Card, EmptyState, ErrorState, Loading } from '../../shared/ui/components';
import { useAsync } from '../../shared/ui/useAsync';
import { QrCode } from '../nursery/QrCode';
import { useMe } from '../session/SessionProvider';
import { stageLabel, useVisitorAccess } from './shared';
import { useTabParam } from '../../shared/ui/useTabParam';
import { VisitorForm } from './VisitorForm';

const ACTIVE: VisitorStage[] = ['primera_visita', 'contactado', 'segunda_visita', 'integrandose', 'raices', 'grupo'];
/** Tres filtros; la etapa de cada visita se ve en su tarjeta. */
type Filter = 'activos' | 'miembros' | 'no_continuan';
const FILTERS: [Filter, string, (s: VisitorStage) => boolean][] = [
  ['activos', 'En seguimiento', (s) => ACTIVE.includes(s)],
  ['miembros', 'Ya son miembros', (s) => s === 'miembro'],
  ['no_continuan', 'No continúan', (s) => s === 'no_continua'],
];
const daysAgo = (iso: string) => {
  const [y, m, d] = iso.split('-').map(Number);
  const days = Math.floor((Date.now() - new Date(y!, m! - 1, d!).getTime()) / 86400_000);
  return days <= 0 ? 'hoy' : days === 1 ? 'ayer' : `hace ${days} días`;
};

/** Visitantes: registro rápido en recepción, enlace/QR para que se registren solos y seguimiento. */
export function VisitorsPage({ embedded = false }: { embedded?: boolean }) {
  const me = useMe();
  const churchId = me.activeChurchId;
  const access = useVisitorAccess(churchId);
  const data = useAsync((signal) => (churchId ? api.get<{ items: Visitor[] }>(`/multichurch/churches/${churchId}/visitors`, signal).then((r) => r.items) : Promise.resolve([])), [churchId]);
  const [filter, setFilter] = useTabParam<Filter>(FILTERS.map(([k]) => k), 'etapa');
  const [adding, setAdding] = useState(false);
  const [showQr, setShowQr] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);

  if (!churchId) return <EmptyState title="Elige primero una iglesia en el selector de arriba." />;
  const items = data.status === 'ready' ? data.data : [];
  const matches = (f: Filter) => FILTERS.find(([k]) => k === f)![2];
  const shown = items.filter((v) => matches(filter)(v.stage));
  const link = access.slug ? `${window.location.origin}/visita/${access.slug}` : null;

  return (
    <div className={embedded ? 'stack' : 'page'}>
      <header className="page__header page__header--actions">
        <div>
          {embedded ? null : <h1>Visitas</h1>}
          <p className="muted">Al registrar a alguien, Diaconado y Pastoral reciben el aviso al instante.</p>
        </div>
        {!adding ? <Button onClick={() => setAdding(true)}>Registrar visitante</Button> : null}
      </header>
      {notice ? <Alert tone="success">{notice}</Alert> : null}
      {adding ? (
        <Card title="Registrar visitante">
          <VisitorForm
            submitLabel="Registrar y avisar"
            onSubmit={async (v) => {
              await api.post(`/multichurch/churches/${churchId}/visitors`, v);
              setAdding(false);
              setNotice(`¡Listo! Avisamos a Diaconado y Pastoral que ${v.givenName} nos visita.`);
              data.reload();
            }}
          />
          <p>
            <Button variant="ghost" onClick={() => setAdding(false)}>
              Cancelar
            </Button>
          </p>
        </Card>
      ) : null}

      {link ? (
        <Card title="Que se registren solos" action={<Button variant="ghost" onClick={() => setShowQr((s) => !s)}>{showQr ? 'Ocultar QR' : 'Ver QR'}</Button>}>
          <p className="small muted">Comparte este enlace o imprime el QR para la entrada. Quien lo use queda registrado y avisamos al equipo.</p>
          <p className="small">
            <a href={link} target="_blank" rel="noopener noreferrer">
              {link}
            </a>
          </p>
          {showQr ? (
            <div className="qr-print">
              <QrCode value={link} label="Código QR para registrarse como visitante" />
              <p className="center">Bienvenido. Escanea para registrarte.</p>
              <Button variant="secondary" onClick={() => window.print()}>
                Imprimir
              </Button>
            </div>
          ) : null}
        </Card>
      ) : null}

      <div className="tabs tabs--scroll" role="tablist" aria-label="Filtrar visitas">
        {FILTERS.map(([k, label, match]) => (
          <button key={k} type="button" role="tab" aria-selected={filter === k} className={`tabs__tab${filter === k ? ' tabs__tab--active' : ''}`} onClick={() => setFilter(k)}>
            {label} ({items.filter((v) => match(v.stage)).length})
          </button>
        ))}
      </div>

      {data.status === 'loading' ? <Loading /> : null}
      {data.status === 'error' ? <ErrorState error={data.error} onRetry={data.reload} /> : null}
      {data.status === 'ready' && shown.length === 0 ? (
        <Card>
          <EmptyState title="No hay visitantes aquí." />
        </Card>
      ) : null}
      {shown.length > 0 ? (
        <ul className="list">
          {shown.map((v) => (
            <li key={v.id} className="list__item">
              <span className="agenda__body">
                <Link to={`/app/visitantes/${v.id}`}>
                  <strong>
                    {v.givenName} {v.familyName}
                  </strong>
                </Link>
                <span className="muted small">
                  Primera visita {daysAgo(v.firstVisit)}
                  {v.invitedBy ? ` · Invitado por ${v.invitedBy}` : ''}
                  {v.follower ? ` · Seguimiento: ${v.follower.name}` : ' · Sin responsable'}
                </span>
              </span>
              <span className="badge">{stageLabel(v.stage)}</span>
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}
